import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getTransactions } from "src/queries/query";
import { columntype, searchParamsToString } from "src/helper/publicFunction";
import DynamicTable from "src/components/table";
import TableSkeleton from "src/components/skeletons/TableSkeleton";
import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import EmptyTable from "src/components/shared/EmptyTable";
import { useTheme } from "@mui/material";

const TransactionPage = () => {
  const theme = useTheme();
  const isLightMode = theme.palette.mode === "light";
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selected, setSelected] = useState([]);
  const [searchParams, setSearchParams] = useState({
    limit: rowsPerPage,
    page,
    sortBy: "title",
    sortDirection: "desc",
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ["transactions", searchParamsToString(searchParams)],
    queryFn: () =>
      getTransactions({ params: searchParamsToString(searchParams) }),
  });

  // Process API transaction data into UI consumable format
  const processedItems =
    data?.data?.map((tx) => ({
      _id: tx._id,
      transactionId: tx.transactionId,
      amount: tx.amount,
      currency: tx.currency,
      createdAt: tx.createdAt,
      status: tx.status,
      isSuccess: tx.isSuccess,
      gateway: tx.gateway,
      decision: tx.decision,
      userName: tx.user
        ? `${tx.user.firstName ?? ""} ${tx.user.lastName ?? ""}`.trim()
        : "",
      userEmail: tx.user?.email ?? "",
      userPhone:
        tx.user?.phone?.code && tx.user?.phone?.number
          ? `+${tx.user.phone.code}${tx.user.phone.number}`
          : "",
      driverName: tx.driver
        ? `${tx.driver.firstName ?? ""} ${tx.driver.lastName ?? ""}`.trim()
        : "",
      driverEmail: tx.driver?.email ?? "",
      driverPhone:
        tx.driver?.phone?.code && tx.driver?.phone?.number
          ? `+${tx.driver.phone.code}${tx.driver.phone.number}`
          : "",
      orderReference: tx.orderReference,
      orderStatus: tx.order?.status ?? "",
      paidUsd: tx.order?.paidUsd,
      orderDateTime: tx.order?.orderDateTime,
    })) ?? [];

  const columns = [
    { type: columntype.text, name: "transactionId", label: "Transaction ID" },
    { type: columntype.text, name: "amount", label: "Amount" },
    { type: columntype.text, name: "currency", label: "Currency" },
    { type: columntype.text, name: "createdAt", label: "Created At" },
    { type: columntype.text, name: "status", label: "Status" },
    { type: columntype.text, name: "decision", label: "Decision" },
    { type: columntype.text, name: "gateway", label: "Gateway" },
    { type: columntype.text, name: "userName", label: "User" },
    { type: columntype.text, name: "userEmail", label: "User Email" },
    { type: columntype.text, name: "userPhone", label: "User Phone" },
    { type: columntype.text, name: "driverName", label: "Driver" },
    { type: columntype.text, name: "driverEmail", label: "Driver Email" },
    { type: columntype.text, name: "driverPhone", label: "Driver Phone" },
    { type: columntype.text, name: "orderReference", label: "Order Reference" },
    { type: columntype.text, name: "orderStatus", label: "Order Status" },
    { type: columntype.text, name: "paidUsd", label: "Paid (USD)" },
    { type: columntype.text, name: "orderDateTime", label: "Order Date/Time" },
  ];

  const headCells = [
    { id: "transactionId", label: "Transaction ID", withSort: true },
    { id: "amount", label: "Amount", withSort: true },
    { id: "currency", label: "Currency", withSort: true },
    { id: "createdAt", label: "Created At", withSort: true },
    { id: "status", label: "Status", withSort: true },
    { id: "decision", label: "Decision", withSort: true },
    { id: "gateway", label: "Gateway", withSort: true },
    { id: "userName", label: "User", withSort: true },
    { id: "userEmail", label: "User Email", withSort: false },
    { id: "userPhone", label: "User Phone", withSort: false },
    { id: "driverName", label: "Driver", withSort: true },
    { id: "driverEmail", label: "Driver Email", withSort: false },
    { id: "driverPhone", label: "Driver Phone", withSort: false },
    { id: "orderReference", label: "Order Reference", withSort: true },
    { id: "orderStatus", label: "Order Status", withSort: true },
    { id: "paidUsd", label: "Paid (USD)", withSort: true },
    { id: "orderDateTime", label: "Order Date/Time", withSort: true },
  ].filter(Boolean);

  useEffect(() => {
    setSearchParams((prev) => ({
      ...prev,
      limit: rowsPerPage,
      page: page,
    }));
  }, [page, rowsPerPage]);

  console.log(data);
  return (
    <Box
      sx={{
        backgroundColor: "transparent",
        width: "100%",
      }}
    >
      <Box
        sx={{
          border: isLightMode ? "1px solid #dfe7ff" : "1px solid #333F55",
          borderRadius: "4px;",
          marginBottom: "30px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "start",
            justifyContent: "space-between",
            width: "100%",
            borderBottom: isLightMode
              ? "1px solid #dfe7ff"
              : "1px solid #333F55",
            padding: "20px",
            borderRadius: "0px",
            flexDirection: {
              xs: "column",
              md: "row",
            },
            alignItems: {
              xs: "center",
              md: "start",
            },
          }}
        >
          <Typography
            variant="h1"
            sx={{
              color: isLightMode ? "#303030" : "#fff",
              fontWeight: "bold",
              fontSize: "1.44rem",
            }}
          >
            Transaction List
          </Typography>
        </Box>
        <Box
          sx={{
            padding: "20px",
            width: "100%",
            gap: "20px",
            display: "flex",
            flexDirection: "column",
            alignItems: "start",
          }}
        >
          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            sx={{ marginTop: "20px", gap: "20px" }}
          >
            <Grid
              item
              sx={{
                width: "100px",
              }}
            >
              <FormControl
                sx={{
                  width: "100%",
                }}
              >
                <InputLabel>Show</InputLabel>
                <Select
                  onChange={(event) => setRowsPerPage(event.target.value)}
                  defaultValue={10}
                  value={searchParams.limit ?? rowsPerPage}
                  label="Show"
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item>
              <TextField
                value={searchParams.term ?? ""}
                onChange={(e) => {
                  setSearchParams((prev) => ({
                    ...prev,
                    term: e.target.value,
                  }));
                  setPage(1);
                }}
                placeholder="Search"
                variant="outlined"
                size="small"
              />
            </Grid>
          </Grid>
          {isLoading ? (
            <TableSkeleton />
          ) : processedItems && processedItems.length > 0 ? (
            <DynamicTable
              loadingEditdatatype={false}
              onRequestSort={(tableName, type) => {
                if (tableName && type) {
                  setSearchParams((prev) => {
                    const newSortDirection =
                      prev.sortDirection === type
                        ? type === "asc"
                          ? "desc"
                          : "asc"
                        : type;
                    return {
                      ...prev,
                      sortBy: tableName,
                      sortDirection: newSortDirection,
                    };
                  });
                }
              }} // call api
              onPressDelete={(ListIds) => {}}
              onPressItem={(row) => {}}
              onPressAnyAction={(row, failedName, newData) => {}}
              onPressAdd={() => {}}
              modelName={"Transaction"}
              items={processedItems}
              columns={columns}
              headCells={headCells}
              page={page}
              setPage={setPage}
              selected={selected}
              setSelected={setSelected}
              search={searchParams.term}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              Listdropdown={[]}
              withOption={true}
              rowsPerPageOptions={[5, 10, 25, 50]}
              totalItem={data?.totalCount}
              withDeleteItem={false}
              withEditItem={false}
            />
          ) : (
            <div style={{ width: "100%", textAlign: "center" }}>
              <EmptyTable headCells={headCells} />
            </div>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default TransactionPage;
